import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchUsers } from '../utils/searchService';
import { supabase } from '../pages/supabaseClient';
import LensIcon from '../assets/LENS.png';
import './SearchBar.css';

const ONE_HOUR = 60 * 60;

const SearchBar = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showResults, setShowResults] = useState(false);
    const [loading, setLoading] = useState(false);
    const searchRef = useRef(null);
    const navigate = useNavigate();

    // Debounced search
    useEffect(() => {
        const timer = setTimeout(async () => {
            if (searchQuery.trim().length >= 2) {
                setLoading(true);
                try {
                    const results = await searchUsers(searchQuery);

                    // Fetch profile images for results
                    const resultsWithImages = await Promise.all(
                        results.map(async (user) => {
                            let profileImageUrl = null;

                            if (user.profileImage) {
                                try {
                                    const { data: signed, error } = await supabase.storage
                                        .from('images')
                                        .createSignedUrl(user.profileImage, ONE_HOUR);
                                    if (!error) {
                                        profileImageUrl = signed.signedUrl;
                                    }
                                } catch (err) {
                                    console.error('Error fetching profile image:', err);
                                }
                            } else if (user.id) {
                                // Try to list files in profile-pics/{uid}/ folder
                                try {
                                    const { data: files, error } = await supabase.storage
                                        .from('images')
                                        .list(`profile-pics/${user.id}`, {
                                            limit: 1,
                                            sortBy: { column: 'created_at', order: 'desc' }
                                        });
                                    if (!error && files && files.length > 0) {
                                        const profilePath = `profile-pics/${user.id}/${files[0].name}`;
                                        const { data: signed, error: signError } = await supabase.storage
                                            .from('images')
                                            .createSignedUrl(profilePath, ONE_HOUR);

                                        if (!signError) {
                                            profileImageUrl = signed.signedUrl;
                                        }
                                    }
                                } catch (err) {
                                    console.error('Error listing profile images:', err);
                                }
                            }

                            return {
                                ...user,
                                profileImageUrl
                            };
                        })
                    );

                    setSearchResults(resultsWithImages);
                    setShowResults(true);
                } catch (error) {
                    console.error('Search error:', error);
                    setSearchResults([]);
                } finally {
                    setLoading(false);
                }
            } else {
                setSearchResults([]);
                setShowResults(false);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    // Click outside to close
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchRef.current && !searchRef.current.contains(event.target)) {
                setShowResults(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleUserClick = (userId) => {
        setShowResults(false);
        setSearchQuery('');
        navigate(`/journey/${userId}`);
    };

    return (
        <div className="search-bar-container" ref={searchRef}>
            <div className="search-input-wrapper">
                <img src={LensIcon} alt="Search" className="search-icon" />
                <input
                    type="text"
                    className="search-input"
                    placeholder="Search people..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => searchQuery.trim().length >= 2 && setShowResults(true)}
                />
            </div>

            {showResults && (
                <div className="search-results-dropdown">
                    {loading ? (
                        <div className="search-loading">Searching...</div>
                    ) : searchResults.length > 0 ? (
                        searchResults.map((user) => (
                            <div
                                key={user.id}
                                className="search-result-card"
                                onClick={() => handleUserClick(user.id)}
                            >
                                {user.profileImageUrl ? (
                                    <img
                                        src={user.profileImageUrl}
                                        alt={user.authorName}
                                        className="search-result-avatar"
                                    />
                                ) : (
                                    <div className="search-result-avatar-placeholder">
                                        {(user.authorName || user.email || '?').charAt(0).toUpperCase()}
                                    </div>
                                )}
                                <div className="search-result-info">
                                    <div className="search-result-name">{user.authorName || user.email}</div>
                                    {/* Bio Display */}
                                    {user.bio && (
                                        <div className="search-result-bio" title={user.bio}>
                                            {user.bio.length > 60 ? `${user.bio.substring(0, 60)}...More` : user.bio}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="search-no-results">No users found</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchBar;
